import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button, buttonClasses } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { BRAND_CONFIGURATION_PATH } from "../../../routes/paths";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { completeProviderConnect } from "../services/meetingSettingsService";
import { useMeetingApps } from "../hooks/useMeetingApps";
import { APP_SLUGS, MEETING_APPS, type MeetingAppId } from "../meetingApps";
import MeetingAppMark from "../components/MeetingAppMark";

/**
 * Where a meeting app sends the browser back to once an account has been
 * signed into — Google at `/integrations/google/callback`, Zoom at
 * `/integrations/zoom/callback`.
 *
 * One screen for both, because the handshake is one handshake: the provider
 * returns to this app rather than to the backend, so what lands here is the
 * middle of it rather than the end. The query carries a `code` and the
 * `state` that was sent out with the request, and two calls finish the job,
 * in this order —
 *
 *   `POST /meeting-providers/{slug}/callback/ { code, state }`  links it
 *   `PATCH /meeting-settings/ { <PROVIDER> }`                   turns it on
 *
 * — because linking an account and using it are two different settings, and a
 * workspace that had been walked through a sign-in only to find meeting links
 * still off would rightly call that broken.
 *
 * Which provider is being connected comes in as a prop rather than off the
 * URL: each path is registered with that provider per environment and cannot
 * move without being changed there too, so the route is a fixed string and
 * the page is handed the app it belongs to. A `:slug` segment would accept
 * `/integrations/teams/callback` and then have nothing to do with it.
 *
 * The code is good once and for a few minutes, and is passed straight through
 * without being stored — Zoom's PKCE verifier never comes near this app
 * either; it is the backend's half of the challenge it put in the sign-in
 * URL. That is why the retry below only ever repeats the second call: once
 * the account is linked the code has been spent, and a retry that started
 * from the top would fail on a code that cannot be used again. A failure
 * before the account is linked has no retry at all — the way out of that one
 * is starting again from the card, which the failed state says.
 */

type Phase = "working" | "done" | "failed";

/**
 * What a provider puts in the query when somebody turns the consent screen
 * down. Two spellings because the two providers do not agree on one, and the
 * difference between "you cancelled" and "something went wrong" is worth
 * reading both for.
 */
const DECLINED = ["access_denied", "user_denied"];

export interface MeetingProviderCallbackPageProps {
  /** Which app's sign-in is coming back. Fixed per route. */
  app: MeetingAppId;
}

const MeetingProviderCallbackPage: React.FC<MeetingProviderCallbackPageProps> = ({
  app,
}) => {
  const navigate = useNavigate();
  const getToken = useTokenGetter();
  const [params] = useSearchParams();
  const { enable, accountEmail } = useMeetingApps();

  // The app's own name, from the one list that holds it, so this screen and
  // the card's toasts spell it the same way.
  const name = MEETING_APPS.find((entry) => entry.id === app)?.name ?? "the app";

  const [phase, setPhase] = useState<Phase>("working");
  const [problem, setProblem] = useState<string>("");
  // Whether the account got linked before things went wrong, which decides
  // what the failed state is able to offer.
  const [linked, setLinked] = useState(false);

  const code = params.get("code") ?? "";
  const state = params.get("state") ?? "";
  const refused = params.get("error") ?? "";

  // Once, however many times this mounts. React runs effects twice in
  // development's strict mode, and a code may only be spent once — the second
  // run would fail on a code the first one had already used.
  const started = useRef(false);

  /**
   * The second half on its own: the account is linked and only the switch is
   * left. Also what Try again calls, since the code that got us here has been
   * spent by the time this can fail.
   */
  const turnOn = useCallback(async () => {
    setPhase("working");
    setProblem("");
    try {
      await enable(app);
      setPhase("done");
    } catch (reason) {
      setProblem(
        reason instanceof ApiError
          ? reason.message
          : "We could not reach the server. Check your connection and try again."
      );
      setPhase("failed");
    }
  }, [app, enable]);

  const finish = useCallback(async () => {
    setPhase("working");
    setProblem("");

    // The provider said no before the app was ever asked. Nothing was linked
    // and there is nothing to retry here — the way back is the card.
    if (refused) {
      setLinked(false);
      setProblem(
        DECLINED.includes(refused)
          ? `The ${name} sign-in was cancelled.`
          : `${name} returned an error: ${refused}.`
      );
      setPhase("failed");
      return;
    }

    // Arrived without the handshake. Somebody opened this URL directly, or a
    // link to it was followed out of a history — either way there is nothing
    // here to finish.
    if (!code || !state) {
      setLinked(false);
      setProblem(`This page was opened without a ${name} sign-in to finish.`);
      setPhase("failed");
      return;
    }

    try {
      await completeProviderConnect(getToken, APP_SLUGS[app], code, state);
      setLinked(true);
    } catch (reason) {
      setLinked(false);
      setProblem(
        reason instanceof ApiError
          ? reason.message
          : "We could not reach the server. Check your connection and try again."
      );
      setPhase("failed");
      return;
    }

    // Linked. What is left is the switch, and it retries on its own from here.
    await turnOn();
  }, [app, code, getToken, name, refused, state, turnOn]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void finish();
  }, [finish]);

  const email = accountEmail(app);

  return (
    // Centred in whatever height the shell gives it: this is one statement,
    // not a screen with a table under it, and set at the top of a tall page it
    // would read as a page still loading the rest of itself.
    <div className="flex min-h-[60vh] flex-1 items-center justify-center px-6">
      <div className="w-full max-w-[440px] card-surface border border-line bg-white px-6 py-8 text-center">
        {phase === "working" && (
          <>
            <div className="flex justify-center">
              <MeetingAppMark app={app} size={44} />
            </div>
            <p className="mt-4 text-[17px] 3xl:text-[18px] font-semibold text-[#00424B]">
              Connecting {name}
            </p>
            <p className="mt-1 text-[14px] text-ink-muted">
              Finishing up — this only takes a moment.
            </p>
            {/* The same spinner a button wears, standing on its own: the wait
                is the whole screen here, so there is no button to put it in. */}
            <span
              aria-hidden="true"
              className="mx-auto mt-5 block h-5 w-5 animate-spin rounded-full border-2 border-brand border-t-transparent"
            />
            {/* What the spinner cannot say. Read out on arrival, so the wait
                is announced rather than being a silent pause. */}
            <span role="status" className="sr-only">
              Connecting {name}
            </span>
          </>
        )}

        {phase === "done" && (
          <>
            <div className="flex justify-center">
              <CheckCircle2
                aria-hidden="true"
                className="h-11 w-11 text-[#16A34A]"
                strokeWidth={1.5}
              />
            </div>
            <p className="mt-4 text-[17px] 3xl:text-[18px] font-semibold text-[#00424B]">
              {name} connected
            </p>
            <p className="mt-1 text-[14px] leading-[1.6] text-ink-muted">
              {email ? (
                <>
                  Connected as <span className="text-ink">{email}</span>. New
                  online appointments will get a meeting link automatically.
                </>
              ) : (
                "New online appointments will get a meeting link automatically."
              )}
            </p>
            {/* A link and not a button: it goes somewhere, and it is the one
                thing to do from here. Wearing the button's look because it is
                the screen's only action — see `buttonClasses`. */}
            <Link
              to={BRAND_CONFIGURATION_PATH}
              replace
              className={`${buttonClasses({ size: "md" })} mt-6`}
            >
              Back to Configuration
            </Link>
          </>
        )}

        {phase === "failed" && (
          <>
            <div className="flex justify-center">
              <XCircle
                aria-hidden="true"
                className="h-11 w-11 text-[#FF3B30]"
                strokeWidth={1.5}
              />
            </div>
            <p className="mt-4 text-[17px] 3xl:text-[18px] font-semibold text-[#00424B]">
              {linked ? `Could not turn ${name} on` : `Could not connect ${name}`}
            </p>
            {/* Two different situations and two different sentences. With the
                account linked, all that is left is a switch the card can flip;
                without it, the sign-in has to start again, and offering Try
                again would spend a code that is already gone. */}
            <p className="mt-1 text-[14px] leading-[1.6] text-ink-muted">
              {linked
                ? `Your account was linked, but we could not switch meeting links on. ${problem}`
                : `${problem} You can start again from Configuration.`}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button
                variant={linked ? "secondary" : "primary"}
                size="md"
                onClick={() => navigate(BRAND_CONFIGURATION_PATH, { replace: true })}
              >
                Back to Configuration
              </Button>
              {linked && (
                <Button size="md" onClick={() => void turnOn()}>
                  Try again
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MeetingProviderCallbackPage;
